import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '@env/environment';

import { AdminPartner, AdminPartnerDetail, AuthResponse } from '@app/core/models';
import {
  unwrapAdminData,
  unwrapAdminPartnerDetail,
  unwrapAdminPartners,
  unwrapCommissionRule,
  unwrapCommissionRules
} from '@app/features/admin/admin-api.utils';

export interface AdminPartnerUpsertPayload {
  displayName: string;
  companyName: string;
  email: string;
  phone: string;
  website: string;
  referralCode: string;
  commissionRuleId?: string | null;
  password?: string;
  confirmPassword?: string;
  status?: AdminPartner['status'];
}

export interface CommissionRuleSavePayload {
  id?: string;
  name: string;
  description: string;
  commissionType: 'PERCENTAGE' | 'FIXED';
  value: number;
  isActive: boolean;
  effectiveStartDate?: string | null;
  effectiveEndDate?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AdminPartnerApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/admin`;

  getPartners() {
    return this.http
      .get<unknown>(`${this.baseUrl}/partners`)
      .pipe(map((response) => unwrapAdminPartners(response)));
  }

  createPartner(payload: AdminPartnerUpsertPayload) {
    return this.http
      .post<unknown>(`${this.baseUrl}/partners`, payload)
      .pipe(map((response) => unwrapAdminPartnerDetail(response)));
  }

  getPartner(partnerUserId: string) {
    return this.http
      .get<unknown>(`${this.baseUrl}/partners/${partnerUserId}`)
      .pipe(map((response) => unwrapAdminPartnerDetail(response)));
  }

  updatePartner(partnerUserId: string, payload: AdminPartnerUpsertPayload) {
    return this.http
      .put<unknown>(`${this.baseUrl}/partners/${partnerUserId}`, payload)
      .pipe(map((response) => unwrapAdminPartnerDetail(response)));
  }

  deletePartner(partnerUserId: string) {
    return this.http
      .delete<unknown>(`${this.baseUrl}/partners/${partnerUserId}`)
      .pipe(map((response) => unwrapAdminData<{ success: true }>(response)));
  }

  impersonatePartner(partnerUserId: string, returnUrl?: string) {
    return this.http
      .post<unknown>(`${this.baseUrl}/partners/${partnerUserId}/impersonate`, {
        returnUrl
      })
      .pipe(map((response) => unwrapAdminData<AuthResponse>(response)));
  }

  getCommissionRules() {
    return this.http
      .get<unknown>(`${this.baseUrl}/commission-rules`)
      .pipe(map((response) => unwrapCommissionRules(response)));
  }

  saveCommissionRule(payload: CommissionRuleSavePayload) {
    const request = {
      id: payload.id || null,
      name: payload.name,
      description: payload.description,
      type: payload.commissionType.toLowerCase(),
      value: payload.value,
      active: payload.isActive,
      effectiveStartUtc: payload.effectiveStartDate ?? null,
      effectiveEndUtc: payload.effectiveEndDate ?? null
    };
    if (payload.id) {
      return this.http
        .put<unknown>(`${this.baseUrl}/commission-rules/${payload.id}`, request)
        .pipe(map(unwrapCommissionRule));
    }

    return this.http
      .post<unknown>(`${this.baseUrl}/commission-rules`, request)
      .pipe(map(unwrapCommissionRule));
  }
}
